import React from 'react';
import { AlertCircleIcon, CheckCircleIcon, AlertTriangleIcon } from 'lucide-react';
import { Loan, AlertType } from '../data/mockData';
interface LoanRowProps {
  loan: Loan;
  isStriped: boolean;
}
const getAlertStyle = (alert: AlertType) => {
  switch (alert) {
    case 'Rate Lock Expiring':
    case 'Missing Documents':
    case 'Closing Date Approaching':
      return 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800';
    case 'AWC No Appraisal Back':
    case 'Disclosures Out Not Signed':
    case 'Disclosures Signed Not Submitted':
      return 'bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900/30 dark:text-orange-300 dark:border-orange-800';
    case 'AWC Appraisal Back':
      return 'bg-green-100 text-green-800 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600';
  }
};
const getAlertIcon = (alert: AlertType) => {
  switch (alert) {
    case 'Rate Lock Expiring':
    case 'Missing Documents':
    case 'Closing Date Approaching':
      return <AlertCircleIcon className="w-3 h-3 mr-1 flex-shrink-0" data-id="element-57" />;
    case 'AWC No Appraisal Back':
    case 'Disclosures Out Not Signed':
    case 'Disclosures Signed Not Submitted':
      return <AlertTriangleIcon className="w-3 h-3 mr-1 flex-shrink-0" data-id="element-58" />;
    case 'AWC Appraisal Back':
      return <CheckCircleIcon className="w-3 h-3 mr-1 flex-shrink-0" data-id="element-59" />;
    default:
      return null;
  }
};
export const LoanRow: React.FC<LoanRowProps> = ({
  loan,
  isStriped
}) => {
  const formattedAmount = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0
  }).format(loan.amount);
  return <div className={`flex items-center py-2.5 px-4 text-sm border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-150 ${isStriped ? 'bg-gray-50/50 dark:bg-gray-800/50' : 'bg-white dark:bg-gray-800'}`} data-id="element-60">
      {/* Borrower */}
      <div className="w-44 flex-shrink-0 font-medium text-gray-900 dark:text-gray-100 truncate pr-4 transition-colors duration-200" data-id="element-61">
        {loan.borrowerName}
      </div>

      {/* Amount */}
      <div className="w-28 flex-shrink-0 font-bold text-blue-600 dark:text-blue-400 text-right pr-4" data-id="element-62">
        {formattedAmount}
      </div>

      {/* Loan # */}
      <div className="w-32 flex-shrink-0 font-mono text-xs text-gray-500 dark:text-gray-400 pr-4 transition-colors duration-200" data-id="element-63">
        #{loan.loanNumber}
      </div>

      {/* Officer */}
      <div className="w-36 flex-shrink-0 text-gray-700 dark:text-gray-300 truncate pr-4 transition-colors duration-200" data-id="element-64">
        {loan.loanOfficer}
      </div>

      {/* Property */}
      <div className="flex-1 text-gray-600 dark:text-gray-400 truncate pr-4 text-xs transition-colors duration-200" data-id="element-65">
        {loan.propertyAddress}
      </div>

      {/* Days */}
      <div className="w-16 flex-shrink-0 text-center text-gray-600 dark:text-gray-400 font-medium pr-4 transition-colors duration-200" data-id="element-66">
        {loan.daysInStage}
      </div>

      {/* Alerts */}
      <div className="w-64 flex-shrink-0 flex flex-wrap gap-1.5" data-id="element-67">
        {loan.alerts.map((alert, index) => <span key={index} className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium border whitespace-nowrap transition-colors duration-200 ${getAlertStyle(alert)}`} title={alert} data-id="element-68">
            {getAlertIcon(alert)}
            <span className="truncate max-w-[120px]" data-id="element-69">{alert}</span>
          </span>)}
        {loan.alerts.length === 0 && <span className="text-xs text-gray-400 dark:text-gray-500 italic transition-colors duration-200" data-id="element-70">
            No alerts
          </span>}
      </div>
    </div>;
};